// ============================================================================
// CollectionsSidebar — Sectioned layout: AI Projects + Manual Collections
// ============================================================================

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { MdSearch, MdAdd, MdAutoAwesome, MdEdit, MdCollections, MdClose } from 'react-icons/md';
import AIProjectCard from './AIProjectCard';
import ManualCollectionCard from './ManualCollectionCard';
import type { Collection } from '../../types/collections.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AITheme {
  name: string;
  thumbnail: string;
  collections: Collection[];
  totalProducts: number;
}

interface CollectionsSidebarProps {
  collections: Collection[];
  selectedCollectionId: string | null;
  onSelectCollection: (pkId: string) => void;
  onCreateAI: () => void;
  onCreateManual: () => void;
  loading?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByTheme(collections: Collection[]): {
  aiThemes: AITheme[];
  manualCollections: Collection[];
} {
  const themeMap = new Map<string, Collection[]>();
  const manualCollections: Collection[] = [];

  for (const col of collections) {
    if ((col.creation_type || 'ai') === 'manual') {
      manualCollections.push(col);
    } else {
      const key = col.header || col.title;
      if (!themeMap.has(key)) themeMap.set(key, []);
      themeMap.get(key)!.push(col);
    }
  }

  const aiThemes: AITheme[] = [];
  for (const [name, cols] of themeMap) {
    aiThemes.push({
      name,
      thumbnail: cols[0]?.image || '',
      collections: cols,
      totalProducts: cols.reduce((s, c) => s + (c.products?.length || 0), 0),
    });
  }

  return { aiThemes, manualCollections };
}

// ---------------------------------------------------------------------------
// Create Dropdown
// ---------------------------------------------------------------------------

const CreateDropdown: React.FC<{
  onAI: () => void;
  onManual: () => void;
}> = ({ onAI, onManual }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-[13px] text-white transition-all duration-200 hover:brightness-110 active:scale-[0.97] cursor-pointer"
        style={{ background: 'linear-gradient(135deg, #EE4423 0%, #D63384 100%)' }}
      >
        <MdAdd size={16} />
        Create
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 rounded-xl bg-[#222] border border-white/[0.08] shadow-2xl shadow-black/40 z-50 overflow-hidden">
          <button
            onClick={() => { onAI(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.05] transition-colors cursor-pointer"
          >
            <MdAutoAwesome size={16} className="text-purple-400" />
            <div>
              <p className="text-[13px] font-medium text-white">AI Collection</p>
              <p className="text-[11px] text-gray-500">Generate with AI</p>
            </div>
          </button>
          <div className="h-px bg-white/[0.06]" />
          <button
            onClick={() => { onManual(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.05] transition-colors cursor-pointer"
          >
            <MdEdit size={16} className="text-amber-400" />
            <div>
              <p className="text-[13px] font-medium text-white">Manual Collection</p>
              <p className="text-[11px] text-gray-500">Curate by hand</p>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Sidebar
// ---------------------------------------------------------------------------

const CollectionsSidebar: React.FC<CollectionsSidebarProps> = ({
  collections,
  selectedCollectionId,
  onSelectCollection,
  onCreateAI,
  onCreateManual,
  loading = false,
  isOpen,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());

  // Group collections
  const { aiThemes, manualCollections } = useMemo(
    () => groupByTheme(collections),
    [collections]
  );

  // Search filter
  const filteredAI = useMemo(() => {
    if (!searchQuery.trim()) return aiThemes;
    const q = searchQuery.toLowerCase();
    return aiThemes.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.collections.some((c) => c.title.toLowerCase().includes(q))
    );
  }, [aiThemes, searchQuery]);

  const filteredManual = useMemo(() => {
    if (!searchQuery.trim()) return manualCollections;
    const q = searchQuery.toLowerCase();
    return manualCollections.filter((c) => c.title.toLowerCase().includes(q));
  }, [manualCollections, searchQuery]);

  // Toggle expand
  const handleToggleExpand = useCallback((name: string) => {
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);

  // Auto-expand the theme containing the selected collection
  useEffect(() => {
    if (selectedCollectionId) {
      for (const theme of aiThemes) {
        if (theme.collections.some((c) => c.pk_id === selectedCollectionId)) {
          setExpandedThemes((prev) => {
            if (prev.has(theme.name)) return prev;
            return new Set(prev).add(theme.name);
          });
          break;
        }
      }
    }
  }, [selectedCollectionId, aiThemes]);

  return (
    <div
      className="flex-shrink-0 flex flex-col h-full border-r border-white/[0.06] overflow-hidden transition-all duration-300 ease-in-out"
      style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #141414 100%)',
        width: isOpen ? '320px' : '0px',
        minWidth: isOpen ? '320px' : '0px',
        opacity: isOpen ? 1 : 0,
        borderRightWidth: isOpen ? '1px' : '0px',
      }}
    >
      <div className="w-80 flex flex-col h-full">
        {/* ── Header ── */}
        <div className="px-4 pt-4 pb-3 border-b border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <h2
              className="text-[14px] font-bold text-gray-200 uppercase"
              style={{ letterSpacing: '0.1em' }}
            >
              Collections
            </h2>
            <div className="flex items-center gap-2">
              <CreateDropdown onAI={onCreateAI} onManual={onCreateManual} />
              {/* Close sidebar button */}
              <button
                onClick={onClose}
                className="flex items-center justify-center cursor-pointer"
                title="Close sidebar"
              >
                <MdClose size={20} className="text-gray-500 group-hover:text-gray-200 transition-colors" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-[7px] rounded-lg bg-white/[0.04] border border-white/[0.08] focus-within:border-orange-500/30 transition-colors">
            <MdSearch size={16} className="text-gray-500 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search collections..."
              className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder-gray-500"
            />
          </div>
        </div>

        {/* ── Scrollable List ── */}
        <div
          className="flex-1 overflow-y-auto px-2 pb-4"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.06) transparent' }}
        >
          {loading ? (
            /* Skeleton */
            <div className="space-y-2 p-2 mt-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="animate-pulse flex gap-3 p-2.5 rounded-lg">
                  <div className="w-10 h-10 bg-white/[0.06] rounded-lg" />
                  <div className="flex-1 space-y-1.5 pt-0.5">
                    <div className="h-3 bg-white/[0.06] rounded w-3/4" />
                    <div className="h-2 bg-white/[0.04] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* ── AI PROJECTS Section ── */}
              {filteredAI.length > 0 && (
                <div className="mt-3">
                  {filteredAI.map((theme) => (
                    <AIProjectCard
                      key={theme.name}
                      themeName={theme.name}
                      collections={theme.collections}
                      totalProducts={theme.totalProducts}
                      isExpanded={expandedThemes.has(theme.name)}
                      selectedCollectionId={selectedCollectionId}
                      onToggleExpand={handleToggleExpand}
                      onSelectCollection={onSelectCollection}
                    />
                  ))}
                </div>
              )}

              {/* ── MANUAL COLLECTIONS Section ── */}
              {filteredManual.length > 0 && (
                <div className="mt-4">
                  <p
                    className="px-3 pb-2 text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1.5"
                    style={{ letterSpacing: '0.12em' }}
                  >
                    <MdCollections size={12} className="text-amber-400/70" />
                    Manual Collections
                  </p>
                  {filteredManual.map((col) => (
                    <ManualCollectionCard
                      key={col.pk_id}
                      collection={col}
                      isSelected={selectedCollectionId === col.pk_id}
                      onSelect={onSelectCollection}
                    />
                  ))}
                </div>
              )}

              {/* ── Empty ── */}
              {filteredAI.length === 0 && filteredManual.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/10 to-purple-500/10 flex items-center justify-center mb-3">
                    <MdCollections size={24} className="text-gray-500" />
                  </div>
                  <p className="text-[13px] font-medium text-gray-400 mb-1">
                    {searchQuery ? 'No results' : 'No collections yet'}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {searchQuery ? 'Try a different search' : 'Create your first collection'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

CollectionsSidebar.displayName = 'CollectionsSidebar';
export default CollectionsSidebar;
